import { IUpdateWidgetPropertyByPath, JsonPathEnum, TCommonData, TEventPayload, TMutableWidgetsData, TWidget } from '@libs/types';
import { ConditionActionEnum, TFunctionParsed, parseStringToFunctionObject as oldParseStringToFunctionObject, JoinConditionActionEnum } from './utils/function';
import { convertEnumToJsonPath, convertOldFunctionToNewFunction, parseConditionStringToObject } from './utils/functionParser';
import { jsonPath } from './utils/jsonpath';

export const PROPERTY_PREFIX = 'jsonPath.';

export type TConditions_2 = {
    firstParameter: TParameter_2;
    isLinkToProperty: boolean;
    secondParameter?: TParameter_2;
    conditionAction?: ConditionActionEnum;
    joinConditionAction?: JoinConditionActionEnum;
};
type TParameter_2 = {
    jsonPath?: string;
    internalId?: string;
    value?: TEventPayload['value'];
};
type TFunctionParsed_2 = {
    conditions: Array<TConditions_2>;
    pathToPropertyLink?: TParameter_2;
    isLinkToProperty: false;
    isOldEnabledFunction?: boolean;
} & TFunctionParsed;
export type TFunctionParsedAsLink = {
    isLinkToProperty: true;
    jsonPath: string;
    internalId: string;
};

export type TFunctionParsedWithoutWidgets = Omit<TFunctionParsed_2, 'widgets'>;

export const getValueViaJsonPath = (widget: TWidget<TMutableWidgetsData>, jsPath: string) => {
    try {
        const result = jsonPath(widget, jsPath);
        return result ? result[0] : undefined;
    } catch {
        console.error('Wrong JSONPath expression: ', jsPath);
        return undefined;
    }
};

export const getValueFromParameter = (parameter: TParameter_2, widgets: Record<string, TWidget<TMutableWidgetsData>>) => {
    const widget = widgets[parameter.internalId ?? ''];
    // TODO: need to revert the changes
    if (!!parameter.internalId && !widget) {
        throw new Error('Widget by internalId not found');
    }
    return widget && parameter.jsonPath ? getValueViaJsonPath(widget, parameter.jsonPath) : parameter.value;
};

export const executeCondition = (condition: TConditions_2, widgets: Record<string, TWidget<TMutableWidgetsData>>) => {
    try {
        if (condition.isLinkToProperty) {
            return getValueFromParameter(condition.firstParameter, widgets);
        }
        const a = getValueFromParameter(condition.firstParameter, widgets);
        const b = getValueFromParameter(condition.secondParameter ?? {}, widgets);
        return executeConditionFinal(a, b, condition.conditionAction);
    } catch {
        // TODO: need to revert the changes
        return false;
    }
};

export const executeConditionFinal = (a: unknown, b: unknown, conditionAction?: ConditionActionEnum) => {
    if (conditionAction) {
        switch (conditionAction) {
            case ConditionActionEnum.EQUAL:
                return a === b;
            case ConditionActionEnum.NOT_EQUAL:
                return a !== b;
            case ConditionActionEnum.LESS_THAN:
            case ConditionActionEnum.LESS_THAN_OR_EQUAL:
            case ConditionActionEnum.MORE_THAN:
            case ConditionActionEnum.MORE_THAN_OR_EQUAL:
                if ((typeof a === 'string' && typeof b === 'string') || (typeof a === 'number' && typeof b === 'number')) {
                    if (conditionAction === ConditionActionEnum.LESS_THAN) return a < b;
                    if (conditionAction === ConditionActionEnum.LESS_THAN_OR_EQUAL) return a <= b;
                    if (conditionAction === ConditionActionEnum.MORE_THAN) return a > b;
                    if (conditionAction === ConditionActionEnum.MORE_THAN_OR_EQUAL) return a >= b;
                }
                return false;
        }
    }
    return false;
};

export const executeConditions = (conditions: Array<TConditions_2>, widgets: Record<string, TWidget<TMutableWidgetsData>>) => {
    let result: unknown = false;
    if (Array.isArray(conditions) && conditions.length > 0) {
        let joinConditionAction = conditions[0].joinConditionAction;
        conditions.forEach((condition, index) => {
            const expressionResult = executeCondition(condition, widgets);
            if (index === 0) {
                result = expressionResult;
            } else {
                switch (joinConditionAction) {
                    case JoinConditionActionEnum.AND:
                        result = result && expressionResult;
                        return;
                    case JoinConditionActionEnum.OR:
                        result = result || expressionResult;
                        return;
                }
                joinConditionAction = condition.joinConditionAction;
            }
        });
    }
    return result;
};

export const getFunctionsObject = (functions: TWidget<TCommonData>['functions']) => {
    const _functionObjects = {} as Record<string, TFunctionParsedWithoutWidgets | TFunctionParsedAsLink>;
    const _internalIds = new Set<string>();
    if (functions && Object.values(functions).length > 0) {
        Object.entries(functions)
            .filter(([key, _value]) => key?.startsWith(PROPERTY_PREFIX))
            .forEach(([key, value]) => {
                const functionObject = parseConditionStringToObject(value);
                if (functionObject.isLinkToProperty) {
                    _internalIds.add(functionObject.internalId);
                } else {
                    functionObject.internalIds.forEach((internalId) => _internalIds.add(internalId));
                }
                _functionObjects[key.replace(PROPERTY_PREFIX, '')] = functionObject;
            });
        Object.entries(functions)
            .filter(([key, _value]) => !key?.startsWith(PROPERTY_PREFIX))
            .forEach(([key, value]) => {
                const functionObject = convertOldFunctionToNewFunction(oldParseStringToFunctionObject(value), key);
                functionObject.internalIds.forEach((internalId) => _internalIds.add(internalId));
                _functionObjects[convertEnumToJsonPath(key) as string] = functionObject;
            });
    }
    return { internalIds: _internalIds, functionObjects: _functionObjects };
};
export const updateWidgetPropertyByPathUtils = ({ widget, value, path }: IUpdateWidgetPropertyByPath) => {
    const keys = path?.replace('$.', '')?.split('.');
    if (widget && keys?.length > 0) {
        const lastKey = keys.pop() ?? '';
        const targetParent = keys.reduce(
            (acc, key) => {
                if (acc[key] === undefined) {
                    acc[key] = {};
                }
                return acc[key] as Record<PropertyKey, unknown>;
            },
            widget as Record<PropertyKey, unknown>,
        );

        targetParent[lastKey] = value;
    }
    return widget;
};

export const calculateFunctions = (
    widget: TWidget<TMutableWidgetsData>,
    functionObjects: Record<string, TFunctionParsedWithoutWidgets | TFunctionParsedAsLink>,
    mutableInternalIdWidgets: Record<string, TWidget<TMutableWidgetsData>>,
    funcUpdateWidgetPropertyByPath: (payload: IUpdateWidgetPropertyByPath) => void,
) => {
    Object.entries(functionObjects).forEach(([key, functionObject]) => {
        if (functionObject.isLinkToProperty) {
            const sourceWidget = mutableInternalIdWidgets[functionObject.internalId];
            if (sourceWidget) {
                const widgetValue = getValueViaJsonPath(sourceWidget, functionObject.jsonPath);
                const currentWidgetValue = getValueViaJsonPath(widget, key);
                if (widgetValue !== undefined && currentWidgetValue !== widgetValue) {
                    funcUpdateWidgetPropertyByPath({ widget, value: widgetValue, path: key });
                }
            }
        } else {
            const widgetValue = getValueViaJsonPath(widget, key);
            const result = executeConditions(functionObject.conditions, mutableInternalIdWidgets);
            const finalResult = functionObject.isOldEnabledFunction ? !result : result;
            if (finalResult !== undefined && widgetValue !== finalResult) {
                funcUpdateWidgetPropertyByPath({ widget, value: finalResult, path: key });
                if (key === JsonPathEnum.ENABLED) {
                    funcUpdateWidgetPropertyByPath({ widget, value: !finalResult, path: JsonPathEnum.IS_DISABLED });
                } else if (key === JsonPathEnum.IS_DISABLED) {
                    funcUpdateWidgetPropertyByPath({ widget, value: !finalResult, path: JsonPathEnum.ENABLED });
                }
            }
        }
    });
};
